import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api, { errMsg } from "../../core/api";
import FileViewer from "../../components/FileViewer";
import { useApp } from "../../core/AppContext";
import {
  MONTHS,
  fa,
  formatDate,
  isoToJ,
  monthRange,
  todayIso,
  todayJ,
} from "../../core/format";
import Modal from "../../components/Modal";
import MonthNav from "../../components/MonthNav";
import Select from "../../components/Select";
import Table, { StatusBadge } from "../../components/Table";
import ResponsiveList from "../../components/ResponsiveList";

import JalaliDate from "../../components/JalaliDate";
import PrintReport from "../../components/PrintReport";
import { taskPrint } from "../../core/printConfigs";
// تعداد ماه‌هایی که در تخصیص وظایف نشان داده می‌شود (ماه جاری + ماه‌های بعد)
const MONTHS_AHEAD = 3;

const statusOptions = (meta) =>
  Object.entries(meta?.taskStatus || {}).map(([value, label]) => ({
    value,
    label,
  }));

/* ---------------- شرح اقدامات انجام‌شده + فایل‌های پیوست ---------------- */
const MAX_FILES = 10;
const MAX_MB = 5;
const MIN_CHARS = 10;
const MAX_CHARS = 20000;
const OK_TYPES = ["application/pdf", "image/png", "image/jpeg", "image/webp"];
const sizeText = (b) =>
  b >= 1048576
    ? `${fa((b / 1048576).toFixed(1))} مگابایت`
    : `${fa(Math.max(1, Math.round(b / 1024)))} کیلوبایت`;
const fileIcon = (mime) =>
  mime === "application/pdf" ? "fa-regular fa-file-pdf" : "fa-regular fa-image";

/** مودال کوچک یک‌فیلدی: دلیل درخواست بازگردانی / دلیل رد */
function NoteModal({
  title,
  label,
  hint,
  submitText,
  required = true,
  onClose,
  onSubmit,
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const go = async () => {
    if (required && text.trim().length < 5)
      return setErr("حداقل ۵ کاراکتر وارد کنید");
    setBusy(true);
    setErr("");
    try {
      await onSubmit(text.trim());
    } catch (e) {
      setErr(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <Modal
      small
      title={title}
      onClose={() => !busy && onClose()}
      footer={
        <>
          <button className="btn" onClick={go} disabled={busy}>
            {busy ? "در حال ارسال..." : submitText}
          </button>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            انصراف
          </button>
        </>
      }
    >
      {hint && <div className="alert info">{hint}</div>}
      {err && <div className="alert err">{err}</div>}
      <div className="field">
        <label>{label}</label>
        <textarea
          rows={4}
          maxLength={500}
          value={text}
          disabled={busy}
          onChange={(e) => setText(e.target.value)}
        />
      </div>
    </Modal>
  );
}

/**
 * mode: "complete" = ثبت انجام وظیفه (شرح الزامی؛ بعد از ثبت قفل می‌شود) | "view" = فقط مشاهده
 */
export function TaskReportModal({ task, mode, onClose, onDone }) {
const { toast, confirm, meta } = useApp();
const [printing, setPrinting] = useState(false);
  const readOnly = mode === "view";
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [saved, setSaved] = useState([]); // فایل‌های ذخیره‌شده روی سرور
  const [files, setFiles] = useState([]); // فایل‌های جدید (هنوز ارسال نشده)
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [checklist, setChecklist] = useState([]);

  useEffect(() => {
    let dead = false;
    api
      .get(`/daily-tasks/${task.id}/report`)
      .then(async (r) => {
        if (!dead) {
          setText(r.data.report || "");
          setSaved(r.data.files || []);
          const cl = r.data.checklist || [];
          if (cl.length) {
            setChecklist(cl);
          } else {
            try {
              const jobId = task.jobDescriptionId;
              if (jobId) {
                const { data } = await api.get(
                  `/job-descriptions/${jobId}/checklist`,
                );
                setChecklist(
                  (data.items || []).map((x) => ({
                    itemId: x.id,
                    title: x.title,
                    checked: 0,
                    note: "",
                  })),
                );
              }
            } catch {}
          }
        }
      })
      .catch((e) => {
        if (!dead) setErr(errMsg(e));
      })
      .finally(() => {
        if (!dead) setLoading(false);
      });
    return () => {
      dead = true;
    };
  }, [task.id]);

  const valid = text.trim().length >= MIN_CHARS;
  const hasPhoto =
    saved.some((f) => String(f.mime || "").startsWith("image/")) ||
    files.some((f) => String(f.type || "").startsWith("image/"));

  const addFiles = (e) => {
    const picked = [...(e.target.files || [])];
    e.target.value = "";
    if (!picked.length) return;
    const next = [...files];
    for (const f of picked) {
      if (!OK_TYPES.includes(f.type)) {
        setErr(`«${f.name}»: فقط PDF یا تصویر (PNG، JPG، WEBP) مجاز است`);
        continue;
      }
      if (f.size > MAX_MB * 1024 * 1024) {
        setErr(`«${f.name}»: حجم فایل بیش از ${fa(MAX_MB)} مگابایت است`);
        continue;
      }
      if (
        next.some(
          (x) =>
            x.name === f.name &&
            x.size === f.size &&
            x.lastModified === f.lastModified,
        )
      )
        continue;
      if (saved.length + next.length >= MAX_FILES) {
        setErr(`حداکثر ${fa(MAX_FILES)} فایل مجاز است`);
        break;
      }
      setErr("");
      next.push(f);
    }
    setFiles(next);
  };

  const removeNew = (i) => setFiles((l) => l.filter((_, idx) => idx !== i));

  const removeSaved = async (f) => {
    if (!(await confirm(`فایل «${f.name}» حذف شود؟`))) return;
    try {
      await api.delete(`/daily-tasks/${task.id}/files/${f.id}`);
      setSaved((l) => l.filter((x) => x.id !== f.id));
    } catch (e) {
      toast(errMsg(e), "err");
    }
  };
  const openSaved = (f) =>
    setViewer({ path: `/daily-tasks/${task.id}/files/${f.id}`, name: f.name });

  const submit = async () => {
    if (!valid)
      return setErr(
        `شرح اقدامات انجام‌شده الزامی است (حداقل ${fa(MIN_CHARS)} کاراکتر)`,
      );
    if (!hasPhoto) return setErr("بارگذاری حداقل یک عکس الزامی است");
    setSaving(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("report", text.trim());
      fd.append("checklist", JSON.stringify(checklist));
      files.forEach((f) => fd.append("files", f));
      await api.post(`/daily-tasks/${task.id}/complete`, fd, {
        timeout: 180000,
      });
      toast("وظیفه با شرح اقدامات ثبت شد");
      onDone();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

const footer = readOnly ? (
  <>
    <button className="btn" onClick={() => setPrinting(true)}><i className="fa-solid fa-print" /> چاپ گزارش</button>
    <button className="btn btn-ghost" onClick={onClose}>بستن</button>
  </>
) : (
    <>
      <button
        className="btn"
        onClick={submit}
        disabled={saving || loading || !valid || !hasPhoto}
      >
        {saving ? "در حال ثبت..." : "ثبت و انجام شد"}
      </button>
      <button className="btn btn-ghost" onClick={onClose} disabled={saving}>
        انصراف
      </button>
    </>
  );

  const total = saved.length + files.length;

  return (
    <>
      <Modal
        wide
        title={readOnly ? "شرح اقدامات انجام‌شده" : "ثبت انجام وظیفه"}
        onClose={() => !saving && onClose()}
        footer={footer}
      >
        <div style={{ marginBottom: 12 }}>
          <b>{task.title}</b>
          <div className="hint">
            {formatDate(task.taskDate)}
            {task.employeeName ? ` · ${task.employeeName}` : ""}
          </div>
        </div>
        {err && <div className="alert err">{err}</div>}
        {loading ? (
          <div className="empty">
            <div className="spinner" style={{ margin: "0 auto" }} />
          </div>
        ) : (
          <>
            {mode === "complete" && (
              <div className="alert info">
                برای ثبت «انجام شد»، ابتدا شرح اقدامات انجام‌شده را وارد کنید
                (الزامی است).
                <b>
                  {" "}
                  توجه: پس از ثبت، شرح و فایل‌ها قابل ویرایش نیستند و فقط با
                  درخواست بازگردانی و تأیید ادمین می‌توان آن را اصلاح کرد.
                </b>
              </div>
            )}
            {checklist.length > 0 && (
              <div className="field">
                <label>چک‌لیست وظیفه</label>
                <div style={{ display: "grid", gap: 8 }}>
                  {checklist.map((c, i) => (
                    <div
                      key={i}
                      style={{
                        border: "1px solid var(--border)",
                        borderRadius: 10,
                        padding: "10px 12px",
                        background: c.checked
                          ? "var(--success-soft)"
                          : "var(--card)",
                      }}
                    >
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          margin: 0,
                          cursor: readOnly ? "default" : "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!c.checked}
                          disabled={readOnly || saving}
                          onChange={(e) => {
                            const v = e.target.checked ? 1 : 0;
                            setChecklist((l) =>
                              l.map((x, j) =>
                                j === i ? { ...x, checked: v } : x,
                              ),
                            );
                          }}
                        />
                        <span style={{ flex: 1, fontWeight: 600 }}>
                          {c.title}
                        </span>
                      </label>
                      {!readOnly && (
                        <div style={{ marginTop: 8 }}>
                          <input
                            placeholder="توضیحات (اختیاری)"
                            maxLength={500}
                            value={c.note || ""}
                            disabled={saving}
                            onChange={(e) => {
                              const v = e.target.value;
                              setChecklist((l) =>
                                l.map((x, j) =>
                                  j === i ? { ...x, note: v } : x,
                                ),
                              );
                            }}
                          />
                        </div>
                      )}
                      {readOnly && c.note && (
                        <div
                          className="hint"
                          style={{ marginTop: 6, whiteSpace: "pre-wrap" }}
                        >
                          {c.note}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="field">
              <label>
                شرح اقدامات انجام‌شده
                {!readOnly && <span className="req">*</span>}
              </label>
              {readOnly ? (
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    lineHeight: 2,
                    background: "var(--card-2)",
                    border: "1px solid var(--border)",
                    borderRadius: 10,
                    padding: "10px 14px",
                    minHeight: 60,
                  }}
                >
                  {text || <span className="hint">شرحی ثبت نشده است.</span>}
                </div>
              ) : (
                <>
                  <textarea
                    rows={9}
                    maxLength={MAX_CHARS}
                    value={text}
                    disabled={saving}
                    style={{ minHeight: 190 }}
                    placeholder="اقدامات انجام‌شده را به‌صورت کامل شرح دهید..."
                    onChange={(e) => {
                      setText(e.target.value);
                      setErr("");
                    }}
                  />
                  <div
                    className="hint"
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span
                      style={{
                        color: valid ? "var(--success)" : "var(--danger)",
                      }}
                    >
                      {valid
                        ? "✓ شرح ثبت‌شدنی است"
                        : `حداقل ${fa(MIN_CHARS)} کاراکتر لازم است`}
                    </span>
                    <span>
                      {fa(text.length)} / {fa(MAX_CHARS)}
                    </span>
                  </div>
                </>
              )}
            </div>

            <div className="field">
              <label>
                عکس‌های پیوست <span className="req">*</span>
                {!readOnly &&
                  ` · تا ${fa(MAX_FILES)} فایل، هر کدام حداکثر ${fa(MAX_MB)} مگابایت (PDF یا تصویر)`}
              </label>

              {saved.length === 0 && files.length === 0 && (
                <div className="hint">فایلی پیوست نشده است.</div>
              )}

              <div style={{ display: "grid", gap: 8 }}>
             <div className="file-list">
  {saved.map((f) => (
    <div key={f.id} className="file-row">
      <i className={fileIcon(f.mime)} />
      <span className="fname" title={f.name}>{f.name}</span>
      <small className="hint fsize">{sizeText(f.size)}</small>
      <button type="button" className="icon-btn" title="مشاهده" onClick={() => openSaved(f)}>
        <i className="fa-regular fa-eye" />
      </button>
      {!readOnly && (
        <button type="button" className="icon-btn danger" title="حذف" disabled={saving} onClick={() => removeSaved(f)}>
          <i className="fa-solid fa-trash" />
        </button>
      )}
    </div>
  ))}
  {files.map((f, i) => (
    <div key={`${f.name}-${i}`} className="file-row new">
      <i className={fileIcon(f.type)} />
      <span className="fname" title={f.name}>{f.name}</span>
      <small className="hint fsize">{sizeText(f.size)} · جدید</small>
      <button type="button" className="icon-btn danger" title="حذف از لیست" disabled={saving} onClick={() => removeNew(i)}>
        <i className="fa-solid fa-xmark" />
      </button>
    </div>
  ))}
</div>
              </div>

              {!readOnly && (
                <div style={{ marginTop: 10 }}>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={saving || total >= MAX_FILES}
                    onClick={() => inputRef.current.click()}
                  >
                    <i className="fa-solid fa-paperclip" /> افزودن فایل
                  </button>
                  <input
                    ref={inputRef}
                    type="file"
                    multiple
                    hidden
                    accept="application/pdf,image/png,image/jpeg,image/webp"
                    onChange={addFiles}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </Modal>

      {viewer && <FileViewer {...viewer} onClose={() => setViewer(null)} />}
        {printing && <PrintReport {...taskPrint(task, meta)} onClose={() => setPrinting(false)} />}
    </>
  );
}

/* ---------------- نمای کارمند: وظایف من ---------------- */
function MyTasks() {
  const { meta, toast } = useApp();
  const tj = todayJ();
  const [ym, setYm] = useState({ y: tj.jy, m: tj.jm });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { task, mode }
  const [revert, setRevert] = useState(null); // task
  const range = useMemo(() => monthRange(ym.y, ym.m), [ym]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const today = todayIso();
      const { data } = await api.get("/daily-tasks", {
        params: { from: today, to: today },
      });
      setRows(data.items);
    } catch (e) {
      toast(errMsg(e), "err");
    } finally {
      setLoading(false);
    }
  }, [toast]);
  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (t, status) => {
    try {
      await api.patch(`/daily-tasks/${t.id}`, { status });
      toast("وضعیت وظیفه ثبت شد");
      load();
    } catch (e) {
      toast(errMsg(e), "err");
    }
  };

  const sendRevert = async (reason) => {
    await api.post(`/daily-tasks/${revert.id}/revert-request`, { reason });
    toast("درخواست بازگردانی برای ادمین ارسال شد");
    setRevert(null);
    load();
  };

  const today = todayIso();
  return (
    <>
      <div className="page-head">
        <div>
          <h2>وظایف روزانه من</h2>
          <p>وظایف تخصیص‌یافته در روزهای شیفت شما</p>
        </div>
        {/* <MonthNav ym={ym} onChange={setYm} /> */}
      </div>
      <div className="card">
        <ResponsiveList
          loading={loading}
          rows={rows}
          empty="برای این ماه وظیفه‌ای به شما تخصیص داده نشده است"
          rowClass={(r) =>
            r.taskDate < today
              ? r.status === "done"
                ? "row-done"
                : "row-missed"
              : undefined
          }
          columns={[
            {
              key: "taskDate",
              label: "تاریخ",
              render: (r) => (
                <span>
                  {formatDate(r.taskDate)}{" "}
                  {/* {r.taskDate === today && (
                    <span className="badge bad">امروز</span>
                  )} */}
                </span>
              ),
            },
            {
              key: "title",
              label: "وظیفه",
              render: (r) => (
                <div>
                  <b>{r.title}</b>
                  {r.description && <div className="hint">{r.description}</div>}
                </div>
              ),
            },
            {
              key: "status",
              label: "وضعیت",
              render: (r) => (
                <div style={{ display: "grid", gap: 4, justifyItems: "start" }}>
                  <StatusBadge
                    status={r.status}
                    label={meta?.taskStatus?.[r.status]}
                  />
                  {r.status === "done" && r.revertStatus === "pending" && (
                    <span className="badge warn">
                      در انتظار تأیید بازگردانی
                    </span>
                  )}
                  {r.status === "done" && r.revertStatus === "rejected" && (
                    <span className="badge bad" title={r.revertNote || ""}>
                      بازگردانی رد شد{r.revertNote ? `: ${r.revertNote}` : ""}
                    </span>
                  )}
                  {r.status !== "done" && r.revertStatus === "approved" && (
                    <span className="badge ok">
                      بازگردانی تأیید شد؛ می‌توانید ویرایش کنید
                    </span>
                  )}
                </div>
              ),
            },
          ]}
          mobileTitle={(r) => r.title}
          mobileBadge={(r) => (
            <div style={{ display: "grid", gap: 4, justifyItems: "end" }}>
              <span className="mc-date">
                {formatDate(r.taskDate)}
                {r.taskDate === today ? " · امروز" : ""}
              </span>
              <StatusBadge
                status={r.status}
                label={meta?.taskStatus?.[r.status]}
              />
              {r.status === "done" && r.revertStatus === "pending" && (
                <span className="badge warn">در انتظار بازگردانی</span>
              )}
              {r.status === "done" && r.revertStatus === "rejected" && (
                <span className="badge bad">بازگردانی رد شد</span>
              )}
              {r.status !== "done" && r.revertStatus === "approved" && (
                <span className="badge ok">بازگردانی تأیید شد</span>
              )}
            </div>
          )}
          mobileMeta={(r) =>
            r.description
              ? [
                  {
                    icon: "fa-regular fa-note-sticky",
                    full: true,
                    value: r.description,
                  },
                ]
              : []
          }
          actions={(r) => {
            if (r.taskDate < today) {
              return r.status === "done" ? (
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => setModal({ task: r, mode: "view" })}
                >
                  <i className="fa-regular fa-file-lines" /> مشاهده شرح
                </button>
              ) : (
                <span className="badge bad">مهلت انجام گذشته است</span>
              );
            }
            return (
              <>
                {r.status === "pending" && (
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => setStatus(r, "in_progress")}
                  >
                    شروع
                  </button>
                )}
                {r.status !== "done" && (
                  <button
                    className="btn btn-sm"
                    onClick={() => setModal({ task: r, mode: "complete" })}
                  >
                    <i className="fa-solid fa-check" />{" "}
                    {r.revertStatus === "approved"
                      ? "ویرایش و ثبت مجدد"
                      : "انجام شد"}
                  </button>
                )}
                {r.status === "done" && (
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => setModal({ task: r, mode: "view" })}
                  >
                    <i className="fa-regular fa-file-lines" /> مشاهده شرح
                  </button>
                )}
                {r.status === "done" && r.revertStatus !== "pending" && (
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => setRevert(r)}
                  >
                    <i className="fa-solid fa-rotate-left" /> درخواست بازگردانی
                  </button>
                )}
              </>
            );
          }}
        />
      </div>
      {modal && (
        <TaskReportModal
          task={modal.task}
          mode={modal.mode}
          onClose={() => setModal(null)}
          onDone={() => {
            setModal(null);
            load();
          }}
        />
      )}
      {revert && (
        <NoteModal
          title="درخواست بازگردانی وظیفه"
          label="دلیل درخواست (چه چیزی باید اصلاح شود؟)"
          submitText="ارسال درخواست"
          hint="پس از تأیید ادمین، می‌توانید شرح اقدامات را ویرایش و دوباره ثبت کنید."
          onClose={() => setRevert(null)}
          onSubmit={sendRevert}
        />
      )}
    </>
  );
}
/* ---------------- تخصیص خودکار ---------------- */
function AutoAssign({ emps, jobs }) {
  const { toast } = useApp();
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState("");
  const [selEmps, setSelEmps] = useState([]);
  const [selJobs, setSelJobs] = useState([]);
  const [perDay, setPerDay] = useState("1");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = (arr, setArr, id) =>
    setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  const allEmps = emps.length > 0 && selEmps.length === emps.length;
  const allJobs = jobs.length > 0 && selJobs.length === jobs.length;

  const run = async () => {
    if (!from || !to) return toast("بازه‌ی زمانی را کامل انتخاب کنید", "err");
    if (to < from) return toast("تاریخ پایان نباید قبل از شروع باشد", "err");
    if (!selEmps.length) return toast("حداقل یک کارمند را انتخاب کنید", "err");
    if (!selJobs.length) return toast("حداقل یک وظیفه را انتخاب کنید", "err");
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/daily-tasks/auto-assign", {
        ...(allEmps ? { all: true } : { employeeIds: selEmps }),
        jobDescriptionIds: selJobs,
        from,
        to,
        perDay: Number(perDay),
      });
      setResult(data);
      toast(`${fa(data.created)} وظیفه ثبت شد`);
    } catch (e) {
      toast(errMsg(e), "err");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card card-pad">
      <div className="alert info">
        سیستم برای هر روزِ شیفتِ هر کارمند در بازه‌ی انتخابی، یک وظیفه ثبت
        می‌کند؛ کارکنان هم‌شیفت در یک روز وظیفه‌ی تکراری نمی‌گیرند و وظایف
        به‌صورت چرخشی و عادلانه توزیع می‌شوند.
      </div>

      <div className="box-head">
        <h3 style={{ fontSize: 14 }}>۱) بازه‌ی زمانی (حداکثر ۹۳ روز)</h3>
      </div>
      <div className="grid2" style={{ marginBottom: 8 }}>
        <JalaliDate label="از تاریخ" value={from} onChange={setFrom} />
        <JalaliDate label="تا تاریخ" value={to} onChange={setTo} />
      </div>

      <div className="box-head">
        <h3 style={{ fontSize: 14 }}>۲) کارمندان</h3>
        <button
          className="btn btn-sm btn-ghost"
          onClick={() => setSelEmps(allEmps ? [] : emps.map((e) => e.id))}
        >
          {allEmps ? "لغو انتخاب همه" : "انتخاب همه"}
        </button>
      </div>
      <div className="check-list" style={{ marginBottom: 18 }}>
        {emps.map((e) => (
          <label
            key={e.id}
            className={`check-item ${selEmps.includes(e.id) ? "on" : ""}`}
            style={{ margin: 0 }}
          >
            <input
              type="checkbox"
              checked={selEmps.includes(e.id)}
              onChange={() => toggle(selEmps, setSelEmps, e.id)}
            />
            {e.firstName} {e.lastName}
          </label>
        ))}
      </div>

      <div className="box-head">
        <h3 style={{ fontSize: 14 }}>۳) وظایف</h3>
        <button
          className="btn btn-sm btn-ghost"
          onClick={() => setSelJobs(allJobs ? [] : jobs.map((j) => j.id))}
        >
          {allJobs ? "لغو انتخاب همه" : "انتخاب همه"}
        </button>
      </div>
      <div className="check-list" style={{ marginBottom: 18 }}>
        {jobs.map((j) => (
          <label
            key={j.id}
            className={`check-item ${selJobs.includes(j.id) ? "on" : ""}`}
            style={{ margin: 0 }}
            title={j.description || ""}
          >
            <input
              type="checkbox"
              checked={selJobs.includes(j.id)}
              onChange={() => toggle(selJobs, setSelJobs, j.id)}
            />
            {j.title}
          </label>
        ))}
      </div>

      <div className="field" style={{ maxWidth: 260 }}>
        <label>تعداد وظیفه برای هر نفر در هر روز</label>
        <Select
          value={perDay}
          onChange={setPerDay}
          options={[1, 2, 3].map((n) => ({ value: String(n), label: fa(n) }))}
        />
      </div>

      {selEmps.length > 0 &&
        selJobs.length > 0 &&
        selJobs.length < selEmps.length * Number(perDay) && (
          <div className="hint" style={{ marginBottom: 10 }}>
            اگر در یک روز تعداد هم‌شیفت‌ها از وظایف انتخابی بیشتر باشد، ناچار
            برخی وظایف تکرار می‌شوند؛ وظایف بیشتری انتخاب کنید.
          </div>
        )}

      {result && (
        <div className="alert ok">
          {fa(result.created)} وظیفه برای {fa(result.days)} روز شیفت ثبت شد
          (موارد تکراریِ موجود نادیده گرفته می‌شوند).
          {result.repeatedDays.length > 0 && (
            <div>
              در این روزها تعداد نفرات از وظایف بیشتر بود و ناچار تکرار شد:{" "}
              {result.repeatedDays.slice(0, 10).map(formatDate).join("، ")}
              {result.repeatedDays.length > 10 ? " ..." : ""}
            </div>
          )}
          {result.withoutShift.length > 0 && (
            <div>بدون شیفت در بازه: {result.withoutShift.join("، ")}</div>
          )}
        </div>
      )}
      <button className="btn" onClick={run} disabled={busy}>
        {busy ? "در حال ثبت..." : "ثبت خودکار وظایف"}
      </button>
    </div>
  );
}
/* ---------------- نمای ادمین ---------------- */
function AdminTasks() {
  const { meta, toast, confirm } = useApp();
  const tj = todayJ();
  const [tab, setTab] = useState("assign");

  // ماه انتخاب‌شده فقط برای «فهرست وظایف ثبت‌شده»
  const [ym, setYm] = useState({ y: tj.jy, m: tj.jm });
  const listRange = useMemo(() => monthRange(ym.y, ym.m), [ym]);

  // بازه‌ی تخصیص: از امروز تا پایان ماه سوم
  const assignRange = useMemo(() => {
    let y = tj.jy,
      m = tj.jm + MONTHS_AHEAD - 1;
    while (m > 12) {
      m -= 12;
      y++;
    }
    return { from: todayIso(), to: monthRange(y, m).to };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tj.jy, tj.jm]);

  const [emps, setEmps] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [selEmps, setSelEmps] = useState([]);
  const [selJobs, setSelJobs] = useState([]);
  const [selDays, setSelDays] = useState([]); // روزهای انتخاب‌شده (ISO)
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fEmp, setFEmp] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [viewTask, setViewTask] = useState(null); // مشاهده‌ی شرح اقدامات

  // درخواست‌های بازگردانی
  const [reqs, setReqs] = useState([]);
  const [reqLoading, setReqLoading] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/employees"), api.get("/job-descriptions")])
      .then(([e, j]) => {
        setEmps(e.data.items);
        setJobs(j.data.items);
      })
      .catch((e) => toast(errMsg(e), "err"));
  }, [toast]);

  const loadShifts = useCallback(
    () =>
      api
        .get("/shifts", { params: assignRange })
        .then((r) => setShifts(r.data.items))
        .catch(() => {}),
    [assignRange],
  );
  useEffect(() => {
    loadShifts();
  }, [loadShifts]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/daily-tasks", {
        params: {
          ...listRange,
          employeeId: fEmp || undefined,
          status: fStatus || undefined,
        },
      });
      setList(data.items);
    } catch (e) {
      toast(errMsg(e), "err");
    } finally {
      setLoading(false);
    }
  }, [listRange, fEmp, fStatus, toast]);
  useEffect(() => {
    if (tab === "list") loadList();
  }, [tab, loadList]);

  const loadReqs = useCallback(async () => {
    setReqLoading(true);
    try {
      const { data } = await api.get("/daily-tasks/revert-requests");
      setReqs(data.items);
    } catch (e) {
      toast(errMsg(e), "err");
    } finally {
      setReqLoading(false);
    }
  }, [toast]);
  useEffect(() => {
    loadReqs();
  }, [loadReqs]); // برای نشان دادن تعداد روی تب

  const approveReq = async (r) => {
    if (
      !(await confirm(
        "وظیفه بازگردانده شود و کارمند بتواند شرح را ویرایش کند؟",
        { okText: "تأیید بازگردانی", danger: false },
      ))
    )
      return;
    try {
      await api.post(`/daily-tasks/${r.taskId}/revert-decision`, {
        approve: true,
      });
      toast("بازگردانی تأیید شد");
      loadReqs();
      if (tab === "list") loadList();
    } catch (e) {
      toast(errMsg(e), "err");
    }
  };
  const rejectReq = async (note) => {
    await api.post(`/daily-tasks/${rejectFor.taskId}/revert-decision`, {
      approve: false,
      note,
    });
    toast("درخواست رد شد");
    setRejectFor(null);
    loadReqs();
  };

  const allSelected = emps.length > 0 && selEmps.length === emps.length;
  const toggle = (arr, setArr, id) =>
    setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  // روزهای شیفتِ کارکنان انتخاب‌شده (۳ ماه آینده)
  const availDays = useMemo(() => {
    const set = new Set(
      shifts
        .filter((s) => selEmps.includes(s.employeeId))
        .map((s) => s.shiftDate),
    );
    return [...set].sort();
  }, [shifts, selEmps]);

  // با تغییر کارمندان، انتخاب روزها پاک می‌شود
  useEffect(() => {
    setSelDays([]);
  }, [selEmps]);

  // گروه‌بندی روزها بر اساس ماه و سال شمسی
  const dayGroups = useMemo(() => {
    const g = new Map();
    availDays.forEach((iso) => {
      const j = isoToJ(iso);
      const key = `${j.jy}-${j.jm}`;
      if (!g.has(key)) g.set(key, { key, jy: j.jy, jm: j.jm, days: [] });
      g.get(key).days.push({ iso, jd: j.jd });
    });
    return [...g.values()];
  }, [availDays]);

  const toggleDay = (iso) =>
    setSelDays((s) =>
      s.includes(iso) ? s.filter((x) => x !== iso) : [...s, iso],
    );
  const chosen = selDays.filter((d) => availDays.includes(d));

  const assign = async () => {
    if (!selEmps.length) return toast("حداقل یک کارمند را انتخاب کنید", "err");
    if (!selJobs.length) return toast("حداقل یک وظیفه را انتخاب کنید", "err");
    if (!availDays.length)
      return toast("کارمندان انتخاب‌شده در ۳ ماه آینده شیفتی ندارند", "err");
    if (!chosen.length) return toast("حداقل یک روز را انتخاب کنید", "err");
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/daily-tasks/assign", {
        ...(allSelected ? { all: true } : { employeeIds: selEmps }),
        jobDescriptionIds: selJobs,
        ...assignRange,
        dates: chosen,
      });
      setResult(data);
      toast(`${fa(data.created)} وظیفه ثبت شد`);
    } catch (e) {
      toast(errMsg(e), "err");
    } finally {
      setBusy(false);
    }
  };

  const del = async (t) => {
    if (!(await confirm("این وظیفه حذف شود؟"))) return;
    try {
      await api.delete(`/daily-tasks/${t.id}`);
      loadList();
      loadReqs();
    } catch (e) {
      toast(errMsg(e), "err");
    }
  };

  const nm = (e) => `${e.firstName} ${e.lastName}`;

  return (
    <>
      <div className="page-head">
        <div>
          <h2>وظایف روزانه</h2>
          <p>تخصیص شرح وظایف به کارکنان بر اساس روزهای شیفت</p>
        </div>
        {tab === "list" && <MonthNav ym={ym} onChange={setYm} />}
      </div>
      <div className="tabs">
        <button
          className={`tab-btn ${tab === "assign" ? "on" : ""}`}
          onClick={() => setTab("assign")}
        >
          تخصیص وظایف
        </button>
        <button
          className={`tab-btn ${tab === "auto" ? "on" : ""}`}
          onClick={() => setTab("auto")}
        >
          تخصیص خودکار
        </button>
        <button
          className={`tab-btn ${tab === "list" ? "on" : ""}`}
          onClick={() => setTab("list")}
        >
          فهرست وظایف ثبت‌شده
        </button>
        <button
          className={`tab-btn ${tab === "reverts" ? "on" : ""}`}
          onClick={() => {
            setTab("reverts");
            loadReqs();
          }}
        >
          درخواست‌های بازگردانی{reqs.length > 0 ? ` (${fa(reqs.length)})` : ""}
        </button>
      </div>

      {tab === "assign" && (
        <div className="card card-pad">
          <div className="box-head">
            <h3 style={{ fontSize: 14 }}>۱) کارمندان</h3>
            <button
              className="btn btn-sm btn-ghost"
              onClick={() =>
                setSelEmps(allSelected ? [] : emps.map((e) => e.id))
              }
            >
              {allSelected ? "لغو انتخاب همه" : "انتخاب همه"}
            </button>
          </div>
          <div className="check-list" style={{ marginBottom: 18 }}>
            {emps.map((e) => (
              <label
                key={e.id}
                className={`check-item ${selEmps.includes(e.id) ? "on" : ""}`}
                style={{ margin: 0 }}
              >
                <input
                  type="checkbox"
                  checked={selEmps.includes(e.id)}
                  onChange={() => toggle(selEmps, setSelEmps, e.id)}
                />
                {nm(e)}
              </label>
            ))}
            {emps.length === 0 && (
              <div className="hint">کارمندی ثبت نشده است.</div>
            )}
          </div>

          <div className="box-head">
            <h3 style={{ fontSize: 14 }}>۲) وظایف (از شرح وظایف ثبت‌شده)</h3>
            <button
              className="btn btn-sm btn-ghost"
              onClick={() =>
                setSelJobs(
                  selJobs.length === jobs.length ? [] : jobs.map((j) => j.id),
                )
              }
            >
              {selJobs.length === jobs.length && jobs.length
                ? "لغو انتخاب همه"
                : "انتخاب همه"}
            </button>
          </div>
          <div className="check-list" style={{ marginBottom: 18 }}>
            {jobs.map((j) => (
              <label
                key={j.id}
                className={`check-item ${selJobs.includes(j.id) ? "on" : ""}`}
                style={{ margin: 0 }}
                title={j.description || ""}
              >
                <input
                  type="checkbox"
                  checked={selJobs.includes(j.id)}
                  onChange={() => toggle(selJobs, setSelJobs, j.id)}
                />
                {j.title}
              </label>
            ))}
            {jobs.length === 0 && (
              <div className="hint">
                ابتدا از «اطلاعات پایه ← شرح وظایف» وظیفه تعریف کنید.
              </div>
            )}
          </div>

          <div className="box-head">
            <h3 style={{ fontSize: 14 }}>
              ۳) روزهای شیفت در {fa(MONTHS_AHEAD)} ماه آینده (روزهایی را که
              می‌خواهید وظیفه‌ی آن‌ها ثبت شود انتخاب کنید)
            </h3>
            {availDays.length > 0 && (
              <button
                className="btn btn-sm btn-ghost"
                onClick={() =>
                  setSelDays(
                    chosen.length === availDays.length ? [] : availDays,
                  )
                }
              >
                {chosen.length === availDays.length
                  ? "لغو انتخاب همه‌ی روزها"
                  : "انتخاب همه‌ی روزها"}
              </button>
            )}
          </div>
          {selEmps.length === 0 ? (
            <div className="hint" style={{ marginBottom: 18 }}>
              ابتدا کارمند را انتخاب کنید.
            </div>
          ) : availDays.length === 0 ? (
            <div className="alert info">
              کارمندان انتخاب‌شده در {fa(MONTHS_AHEAD)} ماه آینده شیفتی ندارند.
              ابتدا از «برنامه شیفت کاری» روزها را ثبت کنید.
            </div>
          ) : (
            <div style={{ marginBottom: 18 }}>
              {dayGroups.map((g) => (
                <div key={g.key} style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      fontWeight: 800,
                      color: "var(--primary)",
                      fontSize: 13.5,
                      marginBottom: 6,
                    }}
                  >
                    {MONTHS[g.jm - 1]} {fa(g.jy)}{" "}
                    <span className="hint" style={{ fontWeight: 400 }}>
                      ({fa(g.days.length)} روز شیفت)
                    </span>
                  </div>
                  <div className="chips">
                    {g.days.map((d) => {
                      const on = chosen.includes(d.iso);
                      return (
                        <button
                          key={d.iso}
                          className={`tab-btn ${on ? "on" : ""}`}
                          style={{ padding: "5px 14px" }}
                          title={`${fa(d.jd)} ${MONTHS[g.jm - 1]} ${fa(g.jy)}`}
                          onClick={() => toggleDay(d.iso)}
                        >
                          {fa(d.jd)} {MONTHS[g.jm - 1]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div className="hint">
                {fa(chosen.length)} روز انتخاب شده است.
              </div>
            </div>
          )}

          {result && (
            <div className="alert ok">
              {fa(result.created)} وظیفه‌ی جدید ثبت شد (موارد تکراری نادیده
              گرفته می‌شوند).
              {result.withoutShift.length > 0 && (
                <div>بدون شیفت در بازه: {result.withoutShift.join("، ")}</div>
              )}
            </div>
          )}
          <button
            className="btn"
            onClick={assign}
            disabled={busy || !chosen.length}
          >
            {busy ? "در حال ثبت..." : "ثبت وظایف"}
          </button>
        </div>
      )}
      {tab === "auto" && <AutoAssign emps={emps} jobs={jobs} />}
      {tab === "list" && (
        <>
          <div className="toolbar">
            <div className="field">
              <label>کارمند</label>
              <Select
                value={fEmp}
                onChange={setFEmp}
                searchable
                options={[
                  { value: "", label: "همه" },
                  ...emps.map((e) => ({ value: e.id, label: nm(e) })),
                ]}
              />
            </div>
            <div className="field">
              <label>وضعیت</label>
              <Select
                value={fStatus}
                onChange={setFStatus}
                options={[{ value: "", label: "همه" }, ...statusOptions(meta)]}
              />
            </div>
          </div>
          <div className="card">
            <ResponsiveList
              loading={loading}
              rows={list}
              empty="وظیفه‌ای ثبت نشده است"
              columns={[
                {
                  key: "taskDate",
                  label: "تاریخ",
                  render: (r) => formatDate(r.taskDate),
                },
                { key: "employeeName", label: "کارمند" },
                { key: "title", label: "وظیفه" },
                {
                  key: "status",
                  label: "وضعیت",
                  render: (r) => (
                    <div
                      style={{ display: "grid", gap: 4, justifyItems: "start" }}
                    >
                      <StatusBadge
                        status={r.status}
                        label={meta?.taskStatus?.[r.status]}
                      />
                      {r.status === "done" && r.revertStatus === "pending" && (
                        <span className="badge warn">درخواست بازگردانی</span>
                      )}
                    </div>
                  ),
                },
              ]}
              mobileTitle={(r) => r.title}
              mobileBadge={(r) => (
                <StatusBadge
                  status={r.status}
                  label={meta?.taskStatus?.[r.status]}
                />
              )}
              mobileMeta={(r) => [
                {
                  icon: "fa-solid fa-user",
                  label: "کارمند",
                  value: r.employeeName,
                },
                {
                  icon: "fa-regular fa-calendar",
                  label: "تاریخ",
                  value: formatDate(r.taskDate),
                },
                ...(r.status === "done" && r.revertStatus === "pending"
                  ? [
                      {
                        icon: "fa-solid fa-rotate-left",
                        full: true,
                        value: (
                          <span className="badge warn">درخواست بازگردانی</span>
                        ),
                      },
                    ]
                  : []),
              ]}
              actions={(r) => (
                <>
                  {r.status === "done" && (
                    <button
                      className="icon-btn"
                      title="شرح اقدامات"
                      onClick={() => setViewTask(r)}
                    >
                      <i className="fa-regular fa-file-lines" />
                    </button>
                  )}
                  <button
                    className="icon-btn danger"
                    title="حذف"
                    onClick={() => del(r)}
                  >
                    <i className="fa-solid fa-trash" />
                  </button>
                </>
              )}
            />
          </div>
        </>
      )}

      {tab === "reverts" && (
        <div className="card">
          <ResponsiveList
            loading={reqLoading}
            rows={reqs}
            empty="درخواست بازگردانی در انتظار بررسی وجود ندارد"
            columns={[
              {
                key: "taskDate",
                label: "تاریخ وظیفه",
                render: (r) => formatDate(r.taskDate),
              },
              { key: "employeeName", label: "کارمند" },
              { key: "title", label: "وظیفه" },
              {
                key: "reason",
                label: "دلیل درخواست",
                render: (r) => (
                  <div className="clip" title={r.reason}>
                    {r.reason}
                  </div>
                ),
              },
            ]}
            mobileTitle={(r) => r.title}
            mobileMeta={(r) => [
              {
                icon: "fa-solid fa-user",
                label: "کارمند",
                value: r.employeeName,
              },
              {
                icon: "fa-regular fa-calendar",
                label: "تاریخ",
                value: formatDate(r.taskDate),
              },
              {
                icon: "fa-regular fa-comment",
                full: true,
                label: "دلیل",
                value: r.reason,
              },
            ]}
            actions={(r) => (
              <>
                <button
                  className="icon-btn"
                  title="مشاهده شرح"
                  onClick={() =>
                    setViewTask({
                      id: r.taskId,
                      title: r.title,
                      taskDate: r.taskDate,
                      employeeName: r.employeeName,
                    })
                  }
                >
                  <i className="fa-regular fa-file-lines" />
                </button>
                <button className="btn btn-sm" onClick={() => approveReq(r)}>
                  <i className="fa-solid fa-check" /> تأیید
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => setRejectFor(r)}
                >
                  <i className="fa-solid fa-xmark" /> رد
                </button>
              </>
            )}
          />
        </div>
      )}

      {rejectFor && (
        <NoteModal
          title="رد درخواست بازگردانی"
          label="دلیل رد (برای کارمند نمایش داده می‌شود)"
          submitText="رد درخواست"
          onClose={() => setRejectFor(null)}
          onSubmit={rejectReq}
        />
      )}
      {viewTask && (
        <TaskReportModal
          task={viewTask}
          mode="view"
          onClose={() => setViewTask(null)}
          onDone={() => setViewTask(null)}
        />
      )}
    </>
  );
}

export default function Tasks() {
  const { user } = useApp();
  return user.role === "admin" ? <AdminTasks /> : <MyTasks />;
}

